import { useEffect } from "react";

import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  KPIParam,
  useModelInsightsKPI,
  useModelInsightsKpiTimePeriod,
} from "@features/mmm/hooks/mmm-url-state";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";

import { useSelectedChannels } from "./use-selected-channels";

export const useChannelComparisonModal = () => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const { selectedChannels, setSingleSelectedChannel } = useSelectedChannels();
  const [isChannelComparisonModalOpen, setIsChannelComparisonModalOpen] =
    useQueryParam("cc-modal", withDefault(NumberParam, 0));

  const [selectedKpi] = useModelInsightsKPI();
  const [channelComparisonKpi, setChannelComparisonKpi] = useQueryParam(
    "cc-kpi",
    KPIParam,
    { updateType: "pushIn" },
  );
  // Keeps the selected KPI in sync with the KPI selected in the channel comparison modal
  useEffect(() => {
    if (selectedKpi) {
      setChannelComparisonKpi(selectedKpi);
    }
  }, [selectedKpi, setChannelComparisonKpi]);

  const [selectedDateRange] = useModelInsightsKpiTimePeriod();
  const [channelComparisonDateRange, setChannelComparisonDateRange] =
    useQueryParam(
      "cc-date-range",
      withDefault(StringParam, selectedDateRange),
      { updateType: "pushIn" },
    );
  // Keeps the selected date range in sync with the date range selected in the channel comparison modal
  useEffect(() => {
    if (selectedDateRange) {
      setChannelComparisonDateRange(selectedDateRange);
    }
  }, [selectedDateRange, setChannelComparisonDateRange]);
  // Resets the selected KPI and date range when it is closed
  useEffect(() => {
    if (isChannelComparisonModalOpen === 0) {
      setChannelComparisonKpi(selectedKpi);
      setChannelComparisonDateRange(selectedDateRange);
      setSingleSelectedChannel("");
    }
  }, [
    isChannelComparisonModalOpen,
    selectedKpi,
    selectedDateRange,
    setChannelComparisonKpi,
    setChannelComparisonDateRange,
    setSingleSelectedChannel,
  ]);

  const toggleIsChannelComparisonModalOpen = () => {
    setIsChannelComparisonModalOpen(Number(!isChannelComparisonModalOpen));
    trackMMMEvent({
      component: ModelInsightsTrackedComponent.CHANNEL_INSIGHTS_ROW,
      action: "onClick",
      actionInfo: "Toggle expand channel insight",
      data: { selectedChannels: selectedChannels.join(",") },
    });
  };

  const [selectedChannelTabIndex, setSelectedChannelTabIndex] = useQueryParam(
    "cc-tab",
    withDefault(NumberParam, 0),
  );

  const openModalWithChannel = (channel: string) => {
    setSingleSelectedChannel(channel);
    setIsChannelComparisonModalOpen(1);
  };

  return {
    isChannelComparisonModalOpen: Boolean(isChannelComparisonModalOpen),
    setIsChannelComparisonModalOpen: (value: boolean) =>
      setIsChannelComparisonModalOpen(Number(value)),
    toggleIsChannelComparisonModalOpen,
    selectedChannelTabIndex,
    setSelectedChannelTabIndex,
    openModalWithChannel,
    channelComparisonKpi,
    setChannelComparisonKpi,
    channelComparisonDateRange,
    setChannelComparisonDateRange,
  };
};
